import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Tooltip,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { COMPANY, COMPANY_USER_VALUE } from 'common/constants/user';
import { AddUserToFirmResponse } from 'common/types/user';
import { RootStateType } from 'dashboard409a/states/store';
import addCompanyUserToCompanyDashboard from 'dashboard409a/utilities/userManagement';
import { UserField } from 'pages/UserPermissions/components/ManageCompanyUsersLedger/types';
import { translateRoleIntoPermissions } from 'pages/UserPermissions/utils';
import { useAddUserToFirm } from 'services/hooks/useManageUser/useManageUser';
import { CreateNewUserDialogProps, ResponsibleNewUserData } from './types';

const CreateNewUserDialog = ({
  open,
  onClose,
  newUser,
  currentFirmId,
  currentCompanyInfo,
  is409a,
}: CreateNewUserDialogProps) => {
  const [user, setUser] = useState<ResponsibleNewUserData>(newUser);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setUser(newUser);
  }, [newUser]);

  const { mutateAsync: addUserToFirm } = useAddUserToFirm();
  const { currentCompany } = useSelector((state: RootStateType) => state.global);

  const handleChange = useCallback((field: UserField, value: string | boolean) => {
    setUser(prevUser => ({
      ...prevUser,
      [field]: value,
    }));
  }, []);

  const hasError = useMemo(() => !user.firstName || !user.lastName || !user.email, [user]);

  const handleSave = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!user.firstName || !user.lastName || !user.email) {
        throw new Error('First Name, Last Name and Email are required');
      }

      if (is409a && currentCompany?.id) {
        addCompanyUserToCompanyDashboard({ userData: user, currentCompanyId: currentCompany.id, handleDialogClose: onClose });
        return;
      }

      if (!currentFirmId) {
        throw new Error('Firm ID is required');
      }

      const userData = {
        ...user,
        companyId: currentCompanyInfo.company_id,
        role_id: COMPANY_USER_VALUE,
        skip_email: !user.sendActivationEmail,
        firms_permissions: [],
      };
      const firmItem = { itemType: COMPANY, itemId: currentCompanyInfo.company_id ?? null };
      const permissions
        = translateRoleIntoPermissions({
          role: { id: COMPANY_USER_VALUE, firmItem },
          firmId: currentFirmId,
          companyList: [currentCompanyInfo],
          fundList: [],
        }) ?? [];
      const result = await addUserToFirm({ firmId: currentFirmId, userData, permissions }) as AddUserToFirmResponse;
      if (result) {
        enqueueSnackbar('User details saved successfully', { variant: 'success' });
        setError('');
        setUser({} as ResponsibleNewUserData);
        onClose(result.new_user);
      }
    } catch (saveError) {
      const errorMessage
        = saveError instanceof Error
          ? saveError.message
          : 'An error occurred while saving the user details. Please try again.';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [user, is409a, currentCompany, currentFirmId, currentCompanyInfo, addUserToFirm, onClose]);

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Enter user details for {user.fullName}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Email"
          type="text"
          fullWidth
          value={user?.email ?? ''}
          onChange={e => handleChange('email', e.target.value)}
          variant="standard"
        />
        <TextField
          margin="dense"
          label="First Name"
          type="text"
          fullWidth
          value={user?.firstName ?? ''}
          onChange={e => handleChange('firstName', e.target.value)}
          error={!error && !user?.firstName}
          helperText={!!error && !user?.firstName ? error : ''}
          variant="standard"
        />
        <TextField
          margin="dense"
          label="Last Name"
          type="text"
          fullWidth
          value={user?.lastName ?? ''}
          onChange={e => handleChange('lastName', e.target.value)}
          error={!error && !user?.lastName}
          helperText={!!error && !user?.lastName ? error : ''}
          variant="standard"
        />
        {is409a && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={user.isPrimaryContact}
                  onChange={e => handleChange('isPrimaryContact', e.target.checked)}
                  inputProps={{ 'aria-label': 'primary contact' }}
                />
              }
              label="Primary Contact"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={user.sendActivationEmail}
                  onChange={e => handleChange('sendActivationEmail', e.target.checked)}
                  inputProps={{ 'aria-label': 'send activation email' }}
                />
              }
              label="Send Activation Email"
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} color="primary">
          Cancel
        </Button>
        <Tooltip title={hasError ? 'Please fill in all required fields' : ''}>
          <span>
            <Button onClick={handleSave} color="primary" disabled={hasError || isLoading}>
              {isLoading ? <CircularProgress size={24} /> : 'Add User'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default CreateNewUserDialog;
